import Swiper from "swiper";
import {
  A11y,
  Autoplay,
  EffectFade,
  Keyboard,
  Navigation,
  Pagination,
  Thumbs,
} from "swiper/modules";
import type { SwiperOptions } from "swiper/types";
import type { Plugin } from "../core/plugin";
import { PluginRegistry } from "../core/pluginRegistry";

interface SlideData {
  element: HTMLElement;
  labels: string[];
  originalIndex: number;
}

const thumbsRegistry: Record<string, { main?: Carousel; thumbs?: Carousel }> =
  {};

export class Carousel implements Plugin {
  private readonly slidesPerView: number | "auto";
  private readonly spaceBetween: number;
  private readonly autoplay: boolean;
  private readonly autoplayConfig: SwiperOptions["autoplay"] | false;
  private readonly pagination: boolean;
  private readonly paginationClass: string;
  private readonly paginationBulletClass: string;
  private readonly paginationDynamicBullets: boolean;
  private readonly paginationDynamicMainBullets: number;
  private readonly navigation: boolean;
  private readonly navigationClass: string;
  private readonly breakpoints: { [width: number]: SwiperOptions };
  private readonly thumbsGroup?: string;
  private readonly isThumbs: boolean;

  // Accesibilidad
  private readonly containerMessage: string;
  private readonly containerRoleDescriptionMessage: string;

  // Necesarias para funcionamiento filtros con core-carousel sin necesidad tocar dsc-co-slider
  private readonly carouselType: "default" | "core";
  private readonly carouselBaseClass: string;
  private readonly carouselContainerSelector: string;

  private swiperInstance: Swiper | null = null;
  private readonly allSlides: SlideData[] = [];
  private activeFilters: Set<string> = new Set();

  // Edge click nudge (opcional) - lógica específica para carruseles donde
  // el primer/último slide puede quedar "a medias" al hacer click en el card.
  private readonly edgeClickNudgeEnabled: boolean;
  private readonly edgeClickNudgeSelector: string;
  private readonly edgeClickNudgeDurationMs: number;
  private readonly edgeClickNudgeMinVisibleRatio: number = 0.35;
  private readonly edgeClickNudgeMaxVisibleRatio: number = 0.65;
  private readonly edgeClickNudgeTolerancePx: number = 6;

  private fareModalResizeObserver: ResizeObserver | null = null;
  /** Alineado con el gap de tarifas en modal (24) cuando todo cabe sin flechas. */
  private readonly fareModalFitsTightSpaceBetween: number = 24;

  // Algunos consumidores (p.ej. Angular) montan el contenedor del carrusel
  // antes de que los slides reales lleguen (datos async), así que el conteo
  // hecho en `init()` puede quedarse corto y dejar `slidesPerView` clampado
  // a 1 para siempre. Observamos el wrapper para recalcular en cuanto
  // aparezcan más slides.
  private slidesCountObserver: MutationObserver | null = null;

  private readonly effect: "slide" | "fade";

  // Opciones añadidas para `CoreCarouselBanner` (sliders de portada migrados desde
  // Pegasus / Owl Carousel). Todas son opt-in por `data-*` y su valor por defecto
  // reproduce exactamente el comportamiento anterior, así que los carruseles ya
  // desplegados no cambian.
  private readonly loop: boolean;
  private readonly rewind: boolean;
  private readonly speed?: number;
  private readonly allowTouchMove: boolean;
  private readonly pauseOnMouseEnter: boolean;

  private autoplayToggleEl: HTMLElement | null = null;
  private reducedMotionQuery: MediaQueryList | null = null;

  // Opt-in con `data-fit-slides-to-width="true"`: cuando hay menos slides que
  // `slidesPerView`, recorta ese valor para que los slides existentes ocupen
  // todo el ancho disponible en vez de dejar la fila corta.
  //
  // Desactivado por defecto a propósito: el ajuste cambia el ancho que Swiper
  // calcula para cada slide, así que solo debe aplicarse donde se haya pedido
  // (ver ItineraryDays). El resto de carruseles mantienen el comportamiento
  // estándar de Swiper.
  private readonly fitSlidesToWidth: boolean;

  constructor(private readonly element: HTMLElement) {
    this.slidesPerView =
      element.dataset.slidesPerView === "auto"
        ? "auto"
        : parseInt(element.dataset.slidesPerView || "1", 10);
    this.spaceBetween = parseInt(element.dataset.spaceBetween || "30", 10);
    // `pauseOnMouseEnter` se lee antes que `autoplayConfig` porque se mezcla dentro.
    // WCAG 2.2.2 (Pause, Stop, Hide): un carrusel con autoplay debe poder pararse;
    // el botón de pausa lo aporta el componente, esto es la ayuda del ratón.
    this.pauseOnMouseEnter = element.dataset.pauseOnMouseEnter === "true";

    const autoplayRaw = element.dataset.autoplay === "true";
    const autoplayConfigRaw = element.dataset.autoplayConfig;
    this.autoplay = autoplayRaw;
    const parsedAutoplayConfig =
      autoplayRaw && autoplayConfigRaw
        ? JSON.parse(autoplayConfigRaw)
        : autoplayRaw
          ? {
              delay: 5000,
              stopOnLastSlide: false,
              waitForTransition: true,
            }
          : false;
    this.autoplayConfig =
      parsedAutoplayConfig && this.pauseOnMouseEnter
        ? { ...parsedAutoplayConfig, pauseOnMouseEnter: true }
        : parsedAutoplayConfig;

    // `loop` y `rewind` son excluyentes en Swiper: si llegan los dos, gana `loop`.
    this.loop = element.dataset.loop === "true";
    this.rewind = !this.loop && element.dataset.rewind === "true";
    this.speed = element.dataset.speed
      ? parseInt(element.dataset.speed, 10)
      : undefined;
    // Por defecto `true` para no alterar los carruseles ya desplegados.
    this.allowTouchMove = element.dataset.allowTouchMove !== "false";
    this.pagination = element.dataset.pagination === "true";
    this.paginationClass =
      element.dataset.paginationClass || "swiper-pagination";
    this.paginationBulletClass = element.dataset.paginationBulletClass || "";
    this.paginationDynamicBullets =
      element.dataset.paginationDynamicBullets === "true";
    this.paginationDynamicMainBullets = parseInt(
      element.dataset.paginationDynamicMainBullets || "1",
      10,
    );
    this.navigation = element.dataset.navigation === "true";
    this.navigationClass = element.dataset.navigationClass || "swiper-button";
    this.breakpoints =
      element.dataset.breakpoints != null
        ? JSON.parse(element.dataset.breakpoints)
        : {};
    this.thumbsGroup = element.dataset.thumbsGroup;
    this.isThumbs = element.dataset.isThumbs === "true";
    this.containerMessage =
      element.dataset.containerMessage ?? "Carrusel de contenido";
    this.containerRoleDescriptionMessage =
      element.dataset.containerRoleDescriptionMessage ?? "Carrusel";

    this.edgeClickNudgeEnabled = element.dataset.edgeClickNudge === "true";
    this.edgeClickNudgeSelector = element.dataset.edgeClickNudgeSelector || "";
    this.edgeClickNudgeDurationMs = parseInt(
      element.dataset.edgeClickNudgeDurationMs || "300",
      10,
    );

    // Lógica de filtros para compatibilidad legacy con dsc-co-slider
    const rawCarouselType = element.dataset.carouselType;
    if (rawCarouselType === "core" || rawCarouselType === "default") {
      this.carouselType = rawCarouselType;
    } else {
      if (rawCarouselType && rawCarouselType.trim().length > 0) {
        console.warn(
          `[Carousel plugin] Invalid data-carousel-type="${rawCarouselType}". ` +
            'Allowed values are "default" | "core". Falling back to "default".',
        );
      }
      this.carouselType = "default";
    }

    this.carouselBaseClass =
      this.carouselType === "core" ? "core-carousel" : "dsc-co-slider";
    this.carouselContainerSelector = `.${this.carouselBaseClass}__container`;

    this.effect = element.dataset.effect === "fade" ? "fade" : "slide";

    this.fitSlidesToWidth = element.dataset.fitSlidesToWidth === "true";
  }

  init(): void {
    const modules = [];
    if (this.autoplay) modules.push(Autoplay);
    if (this.effect === "fade") modules.push(EffectFade);
    if (this.navigation) modules.push(Navigation);
    if (this.pagination) modules.push(Pagination);
    if (this.thumbsGroup && !this.isThumbs) modules.push(Thumbs);
    modules.push(A11y, Keyboard);

    const nextEl =
      this.element.querySelector(`.${this.navigationClass}-next`) ||
      this.element.parentElement?.querySelector(
        `.${this.navigationClass}-next`,
      );

    const prevEl =
      this.element.querySelector(`.${this.navigationClass}-prev`) ||
      this.element.parentElement?.querySelector(
        `.${this.navigationClass}-prev`,
      );

    const totalSlides = this.countSlides();

    this.swiperInstance = new Swiper(this.element, {
      modules,
      slidesPerView: this.clampSlidesPerView(this.slidesPerView, totalSlides),
      spaceBetween: this.spaceBetween,
      watchOverflow: true,
      loop: this.loop,
      rewind: this.rewind,
      allowTouchMove: this.allowTouchMove,
      ...(this.speed !== undefined && !Number.isNaN(this.speed)
        ? { speed: this.speed }
        : {}),
      effect: this.effect,
      fadeEffect: this.effect === "fade" ? { crossFade: true } : undefined,
      thumbs: this.thumbsGroup && !this.isThumbs ? { swiper: null } : undefined,
      autoplay: this.autoplayConfig,
      navigation: this.navigation
        ? {
            nextEl: nextEl as HTMLElement,
            prevEl: prevEl as HTMLElement,
          }
        : undefined,
      pagination: this.pagination
        ? {
            el: this.element.querySelector(
              `.${this.paginationClass}`,
            ) as HTMLElement,
            clickable: true,
            bulletClass: `${this.paginationClass}-bullet ${this.paginationBulletClass}`,
            dynamicBullets: this.paginationDynamicBullets,
            dynamicMainBullets: this.paginationDynamicMainBullets,
          }
        : undefined,
      breakpoints: this.getEffectiveBreakpoints(totalSlides),
      watchSlidesProgress: this.isThumbs,
      slideToClickedSlide: this.isThumbs,
      a11y: {
        enabled: true,
        prevSlideMessage: "Slide anterior",
        nextSlideMessage: "Slide siguiente",
        firstSlideMessage: "Este es el primer slide",
        lastSlideMessage: "Este es el último slide",
        paginationBulletMessage: "Ir al slide {{index}}",
        containerMessage: this.containerMessage,
        containerRoleDescriptionMessage: this.containerRoleDescriptionMessage,
        itemRoleDescriptionMessage: "Slide",
      },
      keyboard: {
        enabled: true,
        onlyInViewport: true,
        pageUpDown: true,
      },
    });

    this.initEdgeClickNudge();
    this.initAutoplayToggle();
    // Temporalmente desactivado para comparar comportamiento del modal de tarifas
    // sin la lógica de ajuste "fits in viewport".
    // this.initFareModalFitsInViewport();

    this.initFilters();
    this.registerThumbs();
    this.observeSlideCount(totalSlides);
  }

  destroy(): void {
    this.slidesCountObserver?.disconnect();
    this.slidesCountObserver = null;

    this.autoplayToggleEl?.removeEventListener(
      "click",
      this.handleAutoplayToggle,
    );
    this.autoplayToggleEl = null;

    this.reducedMotionQuery?.removeEventListener(
      "change",
      this.handleReducedMotionChange,
    );
    this.reducedMotionQuery = null;
  }

  /**
   * WCAG 2.2.2 (Pause, Stop, Hide, nivel A — requisito previo de AA): todo
   * contenido que se mueve solo durante más de 5s necesita un control para
   * pararlo. Los sliders de Pegasus autoreproducían cada 10s sin ninguno.
   *
   * El componente pinta el botón con `data-carousel-autoplay-toggle`; aquí
   * solo se cablea. Si no existe el botón, no pasa nada — por eso esto no
   * afecta a los carruseles ya desplegados.
   */
  private initAutoplayToggle(): void {
    if (!this.autoplay || !this.swiperInstance) return;

    this.autoplayToggleEl = this.element.querySelector(
      "[data-carousel-autoplay-toggle]",
    );
    if (!this.autoplayToggleEl) return;

    this.autoplayToggleEl.addEventListener("click", this.handleAutoplayToggle);

    // `prefers-reduced-motion` arranca en pausa: el usuario ha pedido no ver
    // movimiento automático, pero conserva el botón para reanudar si quiere.
    if (typeof window !== "undefined" && window.matchMedia) {
      this.reducedMotionQuery = window.matchMedia(
        "(prefers-reduced-motion: reduce)",
      );
      this.reducedMotionQuery.addEventListener(
        "change",
        this.handleReducedMotionChange,
      );
      if (this.reducedMotionQuery.matches) {
        this.swiperInstance.autoplay?.stop();
      }
    }

    this.syncAutoplayToggleState();
  }

  /**
   * OJO con `autoplay.paused`: NO significa "el usuario ha pulsado pausa".
   *
   * Swiper tiene dos banderas y solo una es nuestra:
   *   - `running`  → el autoplay está activado. Lo mueven `start()` y `stop()`,
   *                  que es justo lo que hace este botón.
   *   - `paused`   → pausa temporal por interacción: `pauseOnMouseEnter`,
   *                  pestaña en segundo plano... La mueve Swiper por su cuenta.
   *
   * Como el botón vive DENTRO del `.swiper`, al ir a pulsarlo el puntero entra
   * en el carrusel y Swiper ya ha puesto `paused = true`. Mirando las dos
   * banderas, la condición "está sonando" salía falsa y el botón llamaba a
   * `start()` en vez de a `stop()`: el icono cambiaba a "play" pero el autoplay
   * seguía corriendo, y a partir de ahí se quedaba clavado, porque `start()` no
   * limpia `paused` y solo `resume()` lo hace.
   *
   * Por eso aquí solo se mira `running`.
   */
  private readonly handleAutoplayToggle = (): void => {
    const autoplay = this.swiperInstance?.autoplay;
    if (!autoplay) return;

    if (autoplay.running) {
      autoplay.stop();
    } else {
      autoplay.start();
    }

    this.syncAutoplayToggleState();
  };

  private readonly handleReducedMotionChange = (
    event: MediaQueryListEvent,
  ): void => {
    const autoplay = this.swiperInstance?.autoplay;
    if (!autoplay) return;

    if (event.matches) {
      autoplay.stop();
    } else {
      autoplay.start();
    }

    this.syncAutoplayToggleState();
  };

  /**
   * Refleja el estado real de Swiper en el botón: `aria-pressed` para lectores
   * de pantalla, `data-state` para que el CSS conmute el icono, y `aria-label`
   * tomado de los `data-*` del propio botón (así el texto es traducible desde
   * el componente y no queda hardcodeado aquí).
   */
  private syncAutoplayToggleState(): void {
    const toggle = this.autoplayToggleEl;
    const autoplay = this.swiperInstance?.autoplay;
    if (!toggle || !autoplay) return;

    // Solo `running`, nunca `paused`: ver la nota de `handleAutoplayToggle`.
    // El botón informa de si el autoplay está activado, no de si Swiper lo tiene
    // pausado un momento porque el ratón está encima.
    const isPlaying = Boolean(autoplay.running);

    toggle.dataset.state = isPlaying ? "playing" : "paused";
    toggle.setAttribute("aria-pressed", isPlaying ? "false" : "true");

    const label = isPlaying
      ? toggle.dataset.labelPause
      : toggle.dataset.labelPlay;
    if (label) toggle.setAttribute("aria-label", label);
  }

  /**
   * Con un `slidesPerView` numérico fijo, Swiper divide el ancho del
   * contenedor entre ese número sin importar cuántos slides reales existan,
   * así que si hay menos slides que ese valor la fila se queda corta en vez
   * de ocupar el ancho disponible. Contamos los slides reales en el DOM
   * antes de inicializar para poder recortar `slidesPerView` y los
   * `breakpoints` contra ese total.
   */
  private countSlides(): number {
    const wrapper = this.element.querySelector(":scope > .swiper-wrapper");
    return wrapper?.children.length ?? 0;
  }

  private clampSlidesPerView(
    value: number | "auto",
    totalSlides: number,
  ): number | "auto" {
    if (!this.fitSlidesToWidth || value === "auto" || totalSlides <= 0)
      return value;
    return Math.min(value, totalSlides);
  }

  private getEffectiveBreakpoints(totalSlides: number): {
    [width: number]: SwiperOptions;
  } {
    if (!this.fitSlidesToWidth || totalSlides <= 0) return this.breakpoints;

    const effectiveBreakpoints: { [width: number]: SwiperOptions } = {};
    for (const [width, config] of Object.entries(this.breakpoints)) {
      effectiveBreakpoints[Number(width)] =
        typeof config.slidesPerView === "number"
          ? {
              ...config,
              slidesPerView: Math.min(config.slidesPerView, totalSlides),
            }
          : config;
    }
    return effectiveBreakpoints;
  }

  /**
   * Vuelve a observar el wrapper por si llegan más slides después de `init()`
   * (render async). Cuando el conteo cambia, recorta `slidesPerView` y
   * `breakpoints` contra el nuevo total y fuerza a Swiper a reevaluar el
   * breakpoint activo, ya que `setBreakpoint()` de Swiper solo reaplica
   * parámetros cuando cambia el breakpoint, no cuando cambian sus valores.
   */
  private observeSlideCount(initialTotalSlides: number): void {
    if (!this.fitSlidesToWidth) return;

    const wrapper = this.element.querySelector(":scope > .swiper-wrapper");
    if (!wrapper || typeof MutationObserver === "undefined") return;

    let lastKnownTotal = initialTotalSlides;

    this.slidesCountObserver = new MutationObserver(() => {
      const totalSlides = this.countSlides();
      if (totalSlides === lastKnownTotal || !this.swiperInstance) return;
      lastKnownTotal = totalSlides;

      const slidesPerView = this.clampSlidesPerView(
        this.slidesPerView,
        totalSlides,
      );
      const breakpoints = this.getEffectiveBreakpoints(totalSlides);

      this.swiperInstance.params.slidesPerView = slidesPerView;
      this.swiperInstance.params.breakpoints = breakpoints;
      this.swiperInstance.originalParams.slidesPerView = slidesPerView;
      this.swiperInstance.originalParams.breakpoints = breakpoints;
      this.swiperInstance.currentBreakpoint = undefined;
      this.swiperInstance.setBreakpoint();
      this.swiperInstance.update();
    });

    this.slidesCountObserver.observe(wrapper, { childList: true });
  }

  /**
   * Característica solo para `core-carousel--fare-modal`: con `slidesPerView: auto`, Swiper
   * a veces deja 2+ puntos de snap aunque todas las cards quepan, así que el lock nativo
   * no aplica. Aquí ajustamos controles, padding y `spaceBetween` según haya desbordamiento real.
   */
  private initFareModalFitsInViewport(): void {
    if (!this.element.closest(".core-carousel--fare-modal")) {
      return;
    }
    if (!this.swiperInstance) {
      return;
    }

    this.updateFareModalFitsInViewport();
    this.swiperInstance.on("update", () => {
      this.updateFareModalFitsInViewport();
    });
    this.swiperInstance.on("resize", () => {
      this.updateFareModalFitsInViewport();
    });

    if (typeof ResizeObserver === "undefined") {
      return;
    }
    this.fareModalResizeObserver = new ResizeObserver(() => {
      this.swiperInstance?.update();
      this.updateFareModalFitsInViewport();
    });
    this.fareModalResizeObserver.observe(this.element);
  }

  private updateFareModalFitsInViewport(): void {
    const root = this.element.closest(
      ".core-carousel--fare-modal",
    ) as HTMLElement | null;
    if (!root || !this.swiperInstance) {
      return;
    }
    const el = this.swiperInstance.el as HTMLElement;
    const wr = this.swiperInstance.wrapperEl as HTMLElement;
    const modalOverlay = root.closest(
      ".com-modalB2C__overlay",
    ) as HTMLElement | null;
    const fareWidthFitClass = "com-modalB2C__overlay--fareWidthFit";

    if (el.clientWidth < 2) {
      root.classList.remove("core-carousel--fare-fits");
      modalOverlay?.classList.remove(fareWidthFitClass);
      return;
    }

    const cs = getComputedStyle(el);
    const pl = parseFloat(cs.paddingLeft) || 0;
    const pr = parseFloat(cs.paddingRight) || 0;
    const innerW = el.clientWidth - pl - pr;
    // Todo el contenido cabe en el área útil, o en el ancho completo del .swiper (quitando
    // padding extra del modal) — sin desbordamiento, sin fila de flechas, menos `spaceBetween`.
    const tolerance = 2;
    const w = wr.scrollWidth;
    const allSlidesFitInViewport =
      w <= innerW + tolerance ||
      (w <= el.clientWidth + tolerance && pl + pr > 0);
    const tight = this.fareModalFitsTightSpaceBetween;
    if (allSlidesFitInViewport) {
      root.classList.add("core-carousel--fare-fits");
      modalOverlay?.classList.add(fareWidthFitClass);
      if (this.swiperInstance.params.spaceBetween !== tight) {
        this.swiperInstance.params.spaceBetween = tight;
        this.swiperInstance.update();
      }
    } else {
      root.classList.remove("core-carousel--fare-fits");
      modalOverlay?.classList.remove(fareWidthFitClass);
      if (this.swiperInstance.params.spaceBetween !== this.spaceBetween) {
        this.swiperInstance.params.spaceBetween = this.spaceBetween;
        this.swiperInstance.update();
      }
    }
  }

  private initEdgeClickNudge(): void {
    if (!this.edgeClickNudgeEnabled) return;
    if (!this.edgeClickNudgeSelector) return;
    if (!this.swiperInstance) return;

    this.element.addEventListener("click", (event) => {
      if (!this.swiperInstance) return;

      const target = event.target;
      if (
        target instanceof HTMLInputElement &&
        target.type === "radio" &&
        target.checked !== undefined
      ) {
        // Comportamiento normal: si ya se ha clicado el radio,
        // no movemos el carrusel.
        return;
      }

      const clickedEl = target instanceof Element ? target : null;
      const cardEl = clickedEl?.closest(this.edgeClickNudgeSelector);
      if (!cardEl) return;

      const slideEl = cardEl.closest(".swiper-slide") as HTMLElement | null;
      if (!slideEl || !this.element.contains(slideEl)) return;

      if (this.swiperInstance.animating) return;

      const slideElements = Array.from(this.swiperInstance.slides);
      const slideIndex = slideElements.indexOf(slideEl);
      if (slideIndex < 0) return;

      const visibleSlideElements = slideElements.filter((el) => {
        const hEl = el as HTMLElement;
        return (
          hEl.style.display !== "none" &&
          !hEl.classList.contains("swiper-slide-hidden")
        );
      }) as HTMLElement[];

      if (visibleSlideElements.length === 0) return;

      const firstVisible = visibleSlideElements[0];
      const lastVisible = visibleSlideElements[visibleSlideElements.length - 1];
      const isFirst = slideEl === firstVisible;
      const isLast = slideEl === lastVisible;
      if (!isFirst && !isLast) return;

      const containerRect = this.element.getBoundingClientRect();
      const slideRect = slideEl.getBoundingClientRect();

      const slideWidth = slideRect.width;
      if (!slideWidth) return;

      const visibleLeft = Math.max(slideRect.left, containerRect.left);
      const visibleRight = Math.min(slideRect.right, containerRect.right);
      const visibleWidth = Math.max(0, visibleRight - visibleLeft);
      const visibleRatio = visibleWidth / slideWidth;

      const isAboutHalf =
        visibleRatio >= this.edgeClickNudgeMinVisibleRatio &&
        visibleRatio <= this.edgeClickNudgeMaxVisibleRatio;

      const clippedOnLeft =
        slideRect.left < containerRect.left - this.edgeClickNudgeTolerancePx;
      const clippedOnRight =
        slideRect.right > containerRect.right + this.edgeClickNudgeTolerancePx;

      if (isLast && clippedOnRight && isAboutHalf) {
        this.swiperInstance.slideTo(slideIndex, this.edgeClickNudgeDurationMs);
      } else if (isFirst && clippedOnLeft && isAboutHalf) {
        this.swiperInstance.slideTo(slideIndex, this.edgeClickNudgeDurationMs);
      }
    });
  }

  private registerThumbs(): void {
    if (!this.thumbsGroup || !this.swiperInstance) return;

    thumbsRegistry[this.thumbsGroup] ??= {};

    if (this.isThumbs) {
      thumbsRegistry[this.thumbsGroup].thumbs = this;
    } else {
      thumbsRegistry[this.thumbsGroup].main = this;
    }

    this.tryInitThumbs(this.thumbsGroup);
  }

  private tryInitThumbs(groupId: string): void {
    const group = thumbsRegistry[groupId];

    if (!group?.main?.swiperInstance || !group?.thumbs?.swiperInstance) {
      return;
    }

    group.main.swiperInstance.thumbs.swiper = group.thumbs.swiperInstance;

    group.main.swiperInstance.thumbs.init();
    group.main.swiperInstance.thumbs.update(true);
  }

  private initFilters(): void {
    const slideElements = this.element.querySelectorAll(".swiper-slide");
    slideElements.forEach((slideElement, index) => {
      const slide = slideElement as HTMLElement;
      const labelsAttr = slide.dataset.labels || slide.dataset.label || "";
      const labels = labelsAttr
        .split(",")
        .map((label) => label.trim().toLowerCase())
        .filter((label) => label.length > 0);

      this.allSlides.push({
        element: slide,
        labels,
        originalIndex: index,
      });
    });

    this.setupFilterButtons();
  }

  private setupFilterButtons(): void {
    const container = this.element.closest(this.carouselContainerSelector);
    if (!container) {
      return;
    }

    const filterButtons = container.querySelectorAll("[data-filter-label]");

    filterButtons.forEach((button, index) => {
      const filterLabel = (button as HTMLElement).dataset.filterLabel;

      button.addEventListener("click", (event) => {
        event.preventDefault();

        if (filterLabel) {
          this.toggleFilter(filterLabel.toLowerCase());
        }
      });
    });
  }

  public toggleFilter(label: string): void {
    if (this.activeFilters.has(label)) {
      this.activeFilters.clear();
    } else {
      this.activeFilters.clear();
      this.activeFilters.add(label);
    }

    try {
      this.applyFilters();

      this.updateButtonStates();
    } catch (error) {
      console.error("Error in toggleFilter:", error);
    }
  }

  private applyFilters(): void {
    if (!this.swiperInstance) return;

    const filteredSlides = this.getFilteredSlides();

    this.allSlides.forEach((slideData) => {
      const shouldShow = filteredSlides.includes(slideData);

      if (shouldShow) {
        slideData.element.style.display = "";
        slideData.element.classList.remove("swiper-slide-hidden");
      } else {
        slideData.element.style.display = "none";
        slideData.element.classList.add("swiper-slide-hidden");
      }
    });

    this.swiperInstance.update();
    this.swiperInstance.slideTo(0, 0);
  }

  private getFilteredSlides(): SlideData[] {
    if (this.activeFilters.size === 0) {
      return [...this.allSlides].sort(
        (a, b) => a.originalIndex - b.originalIndex,
      );
    }

    const filtered = this.allSlides.filter((slideData) => {
      return Array.from(this.activeFilters).some((filter) =>
        slideData.labels.includes(filter),
      );
    });

    return filtered.sort((a, b) => a.originalIndex - b.originalIndex);
  }

  private updateButtonStates(): void {
    const container = this.element.closest(this.carouselContainerSelector);
    if (!container) {
      return;
    }

    const filterButtons = container.querySelectorAll("[data-filter-label]");

    filterButtons.forEach((button, index) => {
      const filterLabel = (button as HTMLElement).dataset.filterLabel;
      if (filterLabel) {
        const isActive = this.activeFilters.has(filterLabel.toLowerCase());

        const defaultStateClass =
          this.carouselType === "core"
            ? "carousel-filter-state-default"
            : "dsc-co-buttonTagFilter--state-default";
        const selectedStateClass =
          this.carouselType === "core"
            ? "carousel-filter-state-selected"
            : "dsc-co-buttonTagFilter--state-selected";

        if (isActive) {
          button.classList.remove(defaultStateClass);
          button.classList.add(selectedStateClass);
        } else {
          button.classList.remove(selectedStateClass);
          button.classList.add(defaultStateClass);
        }
      }
    });
  }

  public getActiveFilters(): string[] {
    return Array.from(this.activeFilters);
  }

  /**
   * Permite acceder al wrapper Swiper desde otros componentes (p.ej. para
   * comportamientos de UI específicos sobre slides).
   */
  public getSwiperInstance(): Swiper | null {
    return this.swiperInstance;
  }

  public setFilters(filters: string[]): void {
    this.activeFilters.clear();
    if (filters.length > 0) {
      this.activeFilters.add(filters[0].toLowerCase());
    }
    this.applyFilters();
    this.updateButtonStates();
  }

  public clearFilters(): void {
    this.activeFilters.clear();
    this.applyFilters();
    this.updateButtonStates();
  }
}

PluginRegistry.register("carousel", Carousel);
